/*
 * 交互附件渲染器
 *
 * 与「自定义代码块」的区别：
 *   - 代码块（components/blocks/*）：纯展示，AI 文本里的 ```xxx 渲染
 *   - 交互附件（这里）：需要前端响应用户操作（按钮点击、表单提交等）
 *
 * 通过 ChatAttachment 类型驱动，AI 想触发哪种交互就推什么类型的附件。
 */

import React from 'react'
import type { ChatAttachment } from '../../types'
import { RecognitionResultCard } from './RecognitionResultCard'
import { QuickReplies } from './QuickReplies'
import { ProgressSteps } from './ProgressSteps'
import { StatCard } from './StatCard'
import { ExportPanel } from './ExportPanel'
import { TemplateSaveCard } from './TemplateSaveCard'
import { ShareTargets } from './ShareTargets'
import './attachments.scss'

// ─── "文件已生成"统一动作组合 ─────────────────────
// 把单纯的 FileAttachment（只能下载）升级为「导出 + 存模板 + 分享」三件套，
// 文件下载只是其中一个出口。下面是默认的 share targets，
// 之后想按文件类型动态调整再扩 derive 函数即可。

/** 按扩展名给出"导出 XX 文档"的按钮文案（单按钮模式） */
function exportButtonText(ext: string): string {
  switch (ext) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return '导出 Excel 表格'
    case 'pdf':
      return '导出 PDF 文档'
    case 'docx':
    case 'doc':
      return '导出 Word 文档'
    case 'pptx':
    case 'ppt':
      return '导出 PPT 文档'
    default:
      return '导出文档'
  }
}

const DEFAULT_SHARE_TARGETS = [
  { key: 'link', label: '复制链接', emoji: '🔗', tone: 'neutral' as const },
  { key: 'email', label: '邮件', emoji: '✉️', tone: 'blue' as const },
  { key: 'wechat', label: '微信', emoji: '💬', tone: 'green' as const },
  { key: 'dingtalk', label: '钉钉', emoji: '🔔', tone: 'orange' as const }
]

/** 文件名 → 模板推荐名（去掉时间戳/hash 等噪声） */
function deriveTemplateName(fileName: string): string {
  const stem = fileName.replace(/\.[^.]+$/, '')
  // 砍掉 _177xxxx_xxxx / 04-23-1_xxx 这类前缀噪声，剩余文字当模板名
  const cleaned = stem
    .replace(/_\d{8,}/g, '')
    .replace(/_[a-f0-9]{8,}/gi, '')
    .replace(/^\d{2,4}-\d{1,2}(-\d+)?_/, '')
    .trim()
  return cleaned || stem || 'AI 填表模板'
}

/** 用户对附件的操作（如点击按钮、选择格式、点击分享） */
export interface AttachmentAction {
  /** 来源附件类型 */
  type: ChatAttachment['type']
  /** 操作名（如 quick-reply 的 action 字段） */
  action: string
  /** 携带的 payload */
  payload?: Record<string, any>
}

export type AttachmentActionHandler = (action: AttachmentAction) => void

interface AttachmentRendererProps {
  attachment: ChatAttachment
  onAction?: AttachmentActionHandler
}

export const AttachmentRenderer: React.FC<AttachmentRendererProps> = ({
  attachment,
  onAction
}) => {
  const fire = (action: string, payload?: Record<string, any>) => {
    if (onAction) onAction({ type: attachment.type, action, payload })
  }

  switch (attachment.type) {
    case 'recognition-result':
      return <RecognitionResultCard data={attachment.data} />

    case 'quick-replies':
      return (
        <QuickReplies
          replies={attachment.replies}
          onReply={(action, label) => fire(action, { label })}
        />
      )

    case 'progress-steps':
      return <ProgressSteps steps={attachment.steps} />

    case 'stat-card':
      return <StatCard data={attachment.data} />

    case 'export-panel':
      return (
        <ExportPanel
          data={attachment.data}
          onExport={(format) => fire('export', { format })}
        />
      )

    case 'template-save':
      return (
        <TemplateSaveCard
          data={attachment.data}
          onSave={(name) => fire('save-template', { name })}
        />
      )

    case 'share-targets':
      return (
        <ShareTargets
          targets={attachment.targets}
          shareUrl={attachment.shareUrl}
          emailSubject={attachment.emailSubject}
          title={attachment.title}
          description={attachment.description}
          // 真正分享副作用已在组件内部完成；这里仅做埋点回调（不再触发 AI 新轮对话）
          onShare={(target) => fire('share', { target })}
        />
      )

    case 'file': {
      // 把"文件已生成"扩展为"导出 + 存模板 + 分享"三件套，统一交互入口。
      // onAction 触发的 type 用各子组件原本对应的类型，方便上游 handler
      // 不用区分这是独立卡还是 file 包裹下的；payload 里再补一个 fromFile 标记。
      const { name, url, size } = attachment.data
      const ext = (name.split('.').pop() || '').toLowerCase()
      const fireAs = (
        t: ChatAttachment['type'],
        action: string,
        payload?: Record<string, any>
      ) => {
        if (onAction)
          onAction({
            type: t,
            action,
            payload: {
              ...payload,
              fromFile: true,
              sourceUrl: url,
              sourceName: name
            }
          })
      }
      return (
        <div className="aiClerk-att-bundle">
          <ExportPanel
            data={{
              fileName: name,
              fileSize: size,
              buttonText: exportButtonText(ext)
            }}
            onExport={(format) => fireAs('export-panel', 'export', { format })}
          />
          <TemplateSaveCard
            data={{ suggestedName: deriveTemplateName(name) }}
            onSave={(tn) =>
              fireAs('template-save', 'save-template', { name: tn })
            }
          />
          <ShareTargets
            targets={DEFAULT_SHARE_TARGETS}
            shareUrl={url}
            onShare={(target) => fireAs('share-targets', 'share', { target })}
          />
        </div>
      )
    }

    default:
      return null
  }
}
